import {
  LOCAL_TERMINAL_GET_ENDPOINT_CHANNEL,
  LOCAL_TERMINAL_GET_INTEGRATION_ENABLED_CHANNEL,
  LOCAL_TERMINAL_KILL_SESSION_CHANNEL,
  LOCAL_TERMINAL_LIST_SESSIONS_CHANNEL,
  LOCAL_TERMINAL_SESSION_CLOSED_CHANNEL,
  LOCAL_TERMINAL_SESSION_OPENED_CHANNEL,
  LOCAL_TERMINAL_SET_ACTIVE_SESSION_CHANNEL,
  LOCAL_TERMINAL_SET_INTEGRATION_ENABLED_CHANNEL,
  type LocalTerminalEndpoint,
  type LocalTerminalExecResult,
  type LocalTerminalSessionInfo,
  type LocalTerminalSessionOpened,
} from '@electron/shared/local-terminal.types';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronUnsubscribe } from '../base/types';

export type {
  LocalTerminalEndpoint,
  LocalTerminalExecResult,
  LocalTerminalSessionInfo,
  LocalTerminalSessionOpened,
} from '@electron/shared/local-terminal.types';

export class LocalTerminalAPI extends ElectronAPI {
  /** Starts the on-device PTY host if needed and returns where to connect. */
  getEndpoint(): Promise<LocalTerminalEndpoint> {
    return this.invoke<LocalTerminalEndpoint>(
      LOCAL_TERMINAL_GET_ENDPOINT_CHANNEL
    );
  }

  /** Shells alive in the host right now; [] if the host isn't running. */
  listSessions(): Promise<LocalTerminalSessionInfo[]> {
    return this.invoke<LocalTerminalSessionInfo[]>(
      LOCAL_TERMINAL_LIST_SESSIONS_CHANNEL
    );
  }

  /** Kill a host session when the tab has no live socket to ask through. */
  killSession(sessionId: string): Promise<void> {
    return this.invoke<void>(LOCAL_TERMINAL_KILL_SESSION_CHANNEL, sessionId);
  }

  /**
   * Tell main which host session the active tab shows (null when none). Agent-run
   * commands default to that shell's working directory.
   */
  setActiveSession(sessionId: string | null): void {
    if (!this.isElectronAvailable) return;
    this.ipcRenderer.send(LOCAL_TERMINAL_SET_ACTIVE_SESSION_CHANNEL, sessionId);
  }

  /** An agent started a command in the host (headless; see agent-runs.store). */
  /** Whether the Terminal integration (Settings > Integrations) is opted in. */
  getIntegrationEnabled(): Promise<boolean> {
    return this.invoke<boolean>(LOCAL_TERMINAL_GET_INTEGRATION_ENABLED_CHANNEL);
  }

  /** User opt-in for the Terminal integration; main persists and re-advertises. */
  setIntegrationEnabled(enabled: boolean): Promise<void> {
    return this.invoke<void>(
      LOCAL_TERMINAL_SET_INTEGRATION_ENABLED_CHANNEL,
      enabled
    );
  }

  onSessionOpened(
    callback: (event: LocalTerminalSessionOpened) => void
  ): ElectronUnsubscribe {
    return this.addListener<LocalTerminalSessionOpened>({
      eventType: LOCAL_TERMINAL_SESSION_OPENED_CHANNEL,
      callback,
    });
  }

  /** An agent-run command finished; carries the exec result (exit code, output). */
  onSessionClosed(
    callback: (result: LocalTerminalExecResult) => void
  ): ElectronUnsubscribe {
    return this.addListener<LocalTerminalExecResult>({
      eventType: LOCAL_TERMINAL_SESSION_CLOSED_CHANNEL,
      callback,
    });
  }
}
